/** Referencia oficial de Meta con todos los códigos de error de la Cloud API. */
export const META_ERROR_CODES_URL =
  'https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/'

type PlainObject = Record<string, unknown>

const isPlainObject = (value: unknown): value is PlainObject =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

const parseJsonLike = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const firstString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

export interface MessageFailure {
  code: string
  reason: string
}

/**
 * Código y motivo con los que Meta reportó que no entregó un mensaje.
 *
 * `provider_response` guarda el último webhook de estado del mensaje; cuando falló,
 * Meta reporta el error en `entry[0].changes[0].value.statuses[0].errors[0]`. Llega
 * como objeto (la API ya lo parsea) o como texto JSON. Devuelve null si no hay error.
 *
 * Es la misma lógica que `callcenter-frontend`: son dos frontends con su propia copia.
 */
export const getMessageFailure = (providerResponse: unknown): MessageFailure | null => {
  const response = parseJsonLike(providerResponse)
  if (!isPlainObject(response) || !Array.isArray(response.entry)) return null

  const entry = response.entry[0]
  if (!isPlainObject(entry) || !Array.isArray(entry.changes)) return null

  const change = entry.changes[0]
  if (!isPlainObject(change) || !isPlainObject(change.value)) return null

  const statuses = change.value.statuses
  const status = Array.isArray(statuses) ? statuses[0] : null
  const errors = isPlainObject(status) && Array.isArray(status.errors) ? status.errors : []
  const error = errors[0]
  if (!isPlainObject(error)) return null

  const errorData = isPlainObject(error.error_data) ? error.error_data : null
  const reason = firstString(errorData?.details, error.message, error.title)
  const code = error.code == null ? '' : String(error.code).trim()
  return reason || code ? { code, reason } : null
}
